import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { AbcDropDownBoxComponent, AbcTextBoxComponent, _AbcTreeNode } from '@barthauer/abc/ui-components';
import DevExpress from 'devextreme';
import { ValueChangedEvent as DropDownBoxValueChangedEvent } from 'devextreme/ui/drop_down_box';
import { ValueChangedEvent as TextBoxValueChangedEvent } from 'devextreme/ui/text_box';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AbcQueriesEditorService } from '../../../abc-queries-editor.service';
import {
  GeneralPropertiesTranslation,
  ObjectSpaceInfo,
  Property,
} from '../../../viewModels/abc-queries-editor.viewModel';

@Component({
  selector: 'abc-opath-queries-editor-general-properties',
  templateUrl: './general-properties.component.html',
  styleUrls: ['./general-properties.component.scss'],
})
export class GeneralPropertiesComponent implements OnInit, OnDestroy {
  @Input() emptyQueries: () => void;
  @Output() OnObjectSpaceNameChange: EventEmitter<void> = new EventEmitter();
  @Output() OnRootElementNameChange: EventEmitter<void> = new EventEmitter();
  @ViewChild(AbcDropDownBoxComponent) abcDropDownBoxComponent: AbcDropDownBoxComponent;
  @ViewChild('textBox') textBox: AbcTextBoxComponent;

  public objectSpaceInfos: ObjectSpaceInfo[] = [];
  public objectSpaceName: string;
  public rootElementName: string;
  public gridColumns: DevExpress.ui.dxDataGrid.Column[] = [{ dataField: 'name' }];
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  private queries: _AbcTreeNode<Property>;
  private $unsubscribe: Subject<void> = new Subject();

  constructor(
    private abcQueriesEditorService: AbcQueriesEditorService,
    private messageBoxService: MessageBoxService,
    private resourcesService: ResourceManagerService,
    private renderer: Renderer2
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
  }

  public translation: GeneralPropertiesTranslation;

  async ngOnInit(): Promise<void> {
    const res: ResourceSet = await this.resAsync;

    this.translation = {
      title: res.get('generalProperties-title'),
      nameOfRootElement: res.get('generalProperties-name-of-root-element'),
      topic: res.get('generalProperties-topic'),
      confirmTopicChange: res.get('generalProperties-confirm-topic-change'),
    };
    this.objectSpaceInfos = this.abcQueriesEditorService.objectSpaceInfos;
    this.objectSpaceName = this.abcQueriesEditorService.objectSpaceName;
    this.rootElementName = this.abcQueriesEditorService.rootElementName;
    this.queries = this.abcQueriesEditorService.queries;
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  public onOpenSpecialFields(): void {
    const headersElement = document.querySelector('.dx-datagrid-headers');
    if (headersElement) {
      this.renderer.setStyle(headersElement, 'display', 'none');
    }
  }
  public async onRootElementNameChange(e: TextBoxValueChangedEvent): Promise<void> {
    this.rootElementName = e.value;
    this.abcQueriesEditorService.rootElementName = e.value;
    e.event && this.OnRootElementNameChange.emit();
  }

  public async onObjectSpaceNameChange(e: DropDownBoxValueChangedEvent): Promise<void> {
    if (e.previousValue && e.value !== this.abcQueriesEditorService.objectSpaceName) {
      if (this.queries?.children.length) {
        this.showConfirmationBox(e);
      } else {
        this.changeObjectSpaceName(e);
      }
    }
  }

  public refreshObjectSpaceName(name: string): void {
    this.abcQueriesEditorService.objectSpaceName = name;
    this.OnObjectSpaceNameChange.emit();
  }

  private changeObjectSpaceName(e: DropDownBoxValueChangedEvent | TextBoxValueChangedEvent): void {
    if (e.value) {
      this.objectSpaceName = e.value;
      this.abcQueriesEditorService.objectSpaceName !== e.value ? this.refreshObjectSpaceName(e.value) : null;
    }
  }

  private async showConfirmationBox(e: DropDownBoxValueChangedEvent | TextBoxValueChangedEvent): Promise<void> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Warning;
    settings.icon = MessageBoxIcon.Question;
    settings.button = MessageBoxButtons.YesNo;
    settings.message = this.translation.confirmTopicChange;
    this.messageBoxService
      .showMessageBox(settings)
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((result: DialogResult) => {
        switch (result['result']) {
          case DialogResult.Yes:
            this.emptyQueries();
            this.rootElementName = '';
            this.abcQueriesEditorService.rootElementName = '';
            this.changeObjectSpaceName(e);
            return;
          case DialogResult.No:
            this.abcDropDownBoxComponent.value = this.objectSpaceName;
            return;
          default:
            break;
        }
      });
  }
}
